import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { updateSubtask } from '../lib/api'
import { logChange } from '../lib/auditLog'
import { addBusinessDays } from '../lib/businessDays'
import { DEPT_COLORS, darkenHex } from '../lib/deptColors'

const getSubtaskDepts = (s) =>
  s.departments?.length ? s.departments : [s.department || 'MX']

const fmtDate = (iso) => {
  if (!iso) return '—'
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function BulkMilestonePushPanel({ project, projectSubtasks, userProfile, onApplied }) {
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const deptMap = Object.fromEntries(orgDepts.map(d => [d.key, d]))

  const [open, setOpen] = useState(false)
  const [days, setDays] = useState('')
  const [afterDate, setAfterDate] = useState('')
  const [selectedDepts, setSelectedDepts] = useState([])
  const [preview, setPreview] = useState(null)
  const [applying, setApplying] = useState(false)
  const [appliedCount, setAppliedCount] = useState(null)

  const resetPreview = () => { setPreview(null); setAppliedCount(null) }

  const toggleDept = (d) => {
    setSelectedDepts(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])
    resetPreview()
  }

  const computePreview = () => {
    const n = parseInt(days, 10)
    if (!n || isNaN(n)) return
    const affected = (projectSubtasks || [])
      .filter(s => !s.deleted_at && s.start_date)
      .filter(s => !afterDate || s.start_date >= afterDate)
      .filter(s => {
        if (selectedDepts.length === 0) return true
        return selectedDepts.some(d => getSubtaskDepts(s).includes(d))
      })
      .map(s => ({
        subtask: s,
        newStart: addBusinessDays(s.start_date, n),
        newEnd: s.end_date ? addBusinessDays(s.end_date, n) : null,
      }))
      .sort((a, b) => a.newStart.localeCompare(b.newStart))
    setPreview(affected)
    setAppliedCount(null)
  }

  const handleApply = async () => {
    if (!preview?.length) return
    setApplying(true)
    try {
      await Promise.all(
        preview.map(({ subtask, newStart, newEnd }) =>
          updateSubtask(subtask.id, { start_date: newStart, end_date: newEnd })
        )
      )
      await Promise.all(
        preview.map(({ subtask, newStart, newEnd }) =>
          logChange({
            projectId: project.id,
            entityType: 'subtask',
            entityId: subtask.id,
            entityLabel: subtask.task_name,
            action: 'updated',
            oldData: { start_date: subtask.start_date, end_date: subtask.end_date },
            newData: { start_date: newStart, end_date: newEnd },
            userProfile,
          })
        )
      )
      const count = preview.length
      setPreview(null)
      setDays('')
      setAfterDate('')
      setSelectedDepts([])
      setAppliedCount(count)
      onApplied?.()
    } catch (err) {
      console.error('Bulk push failed', err)
    } finally {
      setApplying(false)
    }
  }

  const daysNum = parseInt(days, 10)
  const canPreview = !isNaN(daysNum) && daysNum !== 0

  const overDelivery = preview?.filter(({ newEnd }) =>
    project.departure_date && newEnd && newEnd > project.departure_date
  ) ?? []

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Push Milestones</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="p-4 space-y-4">

          {/* Days */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Business days to shift{' '}
              <span className="text-gray-400 font-normal">(negative to move earlier)</span>
            </label>
            <input
              type="number"
              value={days}
              onChange={e => { setDays(e.target.value); resetPreview() }}
              placeholder="e.g. 3 or −2"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
            />
          </div>

          {/* After date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Only milestones starting on or after{' '}
              <span className="text-gray-400 font-normal">(optional — blank = all)</span>
            </label>
            <input
              type="date"
              value={afterDate}
              onChange={e => { setAfterDate(e.target.value); resetPreview() }}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/20"
            />
          </div>

          {/* Departments */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              Departments{' '}
              <span className="text-gray-400 font-normal">(none selected = all)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {orgDepts.map(({ key: d, hex: dHex }) => {
                const sel = selectedDepts.includes(d)
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDept(d)}
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                      sel
                        ? 'border-transparent'
                        : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
                    }`}
                    style={sel ? { backgroundColor: dHex + '20', color: dHex } : {}}
                  >
                    {sel && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: dHex }} />}
                    {d}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Preview button */}
          <button
            type="button"
            onClick={computePreview}
            disabled={!canPreview}
            className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Preview Changes
          </button>

          {/* Success message */}
          {appliedCount !== null && !preview && (
            <p className="text-sm text-green-600 font-medium text-center">
              {appliedCount} milestone{appliedCount !== 1 ? 's' : ''} updated.
            </p>
          )}

          {/* Preview results */}
          {preview && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">

              {/* Header */}
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-700">
                  {preview.length === 0
                    ? 'No milestones match'
                    : `${preview.length} milestone${preview.length !== 1 ? 's' : ''} will be shifted`}
                </span>
              </div>

              {/* Delivery date warning */}
              {overDelivery.length > 0 && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border-b border-amber-100">
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">{overDelivery.length} milestone{overDelivery.length !== 1 ? 's' : ''}</span>{' '}
                    will extend past the delivery date ({fmtDate(project.departure_date)}). The delivery date will not be changed automatically.
                  </p>
                </div>
              )}

              {/* Milestone rows */}
              {preview.length > 0 && (
                <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                  {preview.map(({ subtask, newStart, newEnd }) => {
                    const depts = getSubtaskDepts(subtask)
                    const pastDelivery = project.departure_date && newEnd && newEnd > project.departure_date
                    return (
                      <div
                        key={subtask.id}
                        className={`flex items-center gap-2 px-3 py-2 text-xs ${pastDelivery ? 'bg-amber-50/50' : ''}`}
                      >
                        {/* Dept badges */}
                        <div className="flex gap-1 flex-shrink-0">
                          {depts.map(d => {
                            const hex = deptMap[d]?.hex || '#9ca3af'
                            return (
                              <span key={d} className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                                style={{ backgroundColor: hex + '33', color: darkenHex(hex) }}>
                                {d}
                              </span>
                            )
                          })}
                        </div>
                        {/* Name */}
                        <span className="flex-1 min-w-0 font-medium text-gray-700 truncate">{subtask.task_name}</span>
                        {/* Date range */}
                        <span className="flex-shrink-0 text-gray-500 tabular-nums whitespace-nowrap">
                          {subtask.end_date
                            ? <>{fmtDate(subtask.start_date)}–{fmtDate(subtask.end_date)} <span className="text-gray-300 mx-1">→</span> {fmtDate(newStart)}–{fmtDate(newEnd)}</>
                            : <>{fmtDate(subtask.start_date)} <span className="text-gray-300 mx-1">→</span> {fmtDate(newStart)}</>
                          }
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Apply button */}
              {preview.length > 0 && (
                <div className="px-3 py-2.5 border-t border-gray-100 bg-gray-50">
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={applying}
                    className="w-full bg-brand-red hover:bg-brand-red-dark disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                  >
                    {applying ? 'Applying…' : `Apply ${preview.length} Change${preview.length !== 1 ? 's' : ''}`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
